import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, CheckCircle, Loader, AlertCircle } from 'lucide-react';
import { createService, sendOTP, verifyOTP } from '../services/api';
import './PostServiceModal.css';

const CATEGORIES = [
  'B2B Cab & Transport',
  'B2B Hotel & Homestay',
  'Tour Operator',
  'Local Guide',
  'Activities & Experiences',
  'DMC',
  'Visa Assistance',
  'B2B Group Tour Service',
  'B2B Travel Package',
  'International Tour Package',
  'Flight Booking Services',
  'Other'
];

const PostServiceModal = ({ onClose }) => {
  const [step, setStep] = useState(1); // 1: Form, 2: OTP, 3: Success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    phoneNumber: '',
    whatsappNumber: '',
    sameAsPhone: false,
    price: '',
    companyName: '',
    state: '',
    city: '',
    email: ''
  });

  // OTP states
  const [otp, setOtp] = useState(['', '', '', '']);
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked,
        whatsappNumber: checked ? prev.phoneNumber : prev.whatsappNumber
      }));
    } else {
      setFormData(prev => {
        const updated = { ...prev, [name]: value };
        if (name === 'phoneNumber' && prev.sameAsPhone) {
          updated.whatsappNumber = value;
        }
        return updated;
      });
    }
    setError('');
  };

  const validateForm = () => {
    if (!formData.companyName.trim()) return 'Company Name is required';
    if (!formData.state.trim()) return 'State is required';
    if (!formData.city.trim()) return 'City is required';

    if (!formData.title.trim()) return 'Service Headline is required';
    if (!formData.category) return 'Please select a category';

    if (!formData.description.trim()) return 'Detailed Description is required';
    if (!formData.phoneNumber.trim()) return 'Calling number is required';
    if (!/^[6-9]\d{9}$/.test(formData.phoneNumber)) return 'Please enter a valid 10-digit mobile number';
    if (!formData.whatsappNumber.trim()) return 'WhatsApp number is required';
    if (!/^[6-9]\d{9}$/.test(formData.whatsappNumber)) return 'Please enter a valid WhatsApp number';
    return null;
  };

  const handleSendOTP = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await sendOTP(formData.phoneNumber, formData.category);

      if (result.success) {
        setStep(2);
        setResendTimer(60);
        setOtp(['', '', '', '']);
      } else {
        setError(result.message || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      console.error('OTP Error:', err);
      setError('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 3) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 4) {
      setError('Please enter the complete 4-digit OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const verifyResult = await verifyOTP(formData.phoneNumber, formData.category, otpCode);

      if (verifyResult.success) {
        // OTP verified, create the service
        await createService({
          title: formData.title,
          description: formData.description,
          category: formData.category,
          phoneNumber: formData.phoneNumber,
          whatsappNumber: formData.whatsappNumber,
          price: formData.price,
          companyName: formData.companyName,
          state: formData.state,
          city: formData.city,
          email: formData.email
        });

        setStep(3);
      } else {
        setError(verifyResult.message || 'Invalid OTP. Please check and try again.');
      }
    } catch (err) {
      console.error('Verification Error:', err);
      setError(err.response?.data?.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;

    setLoading(true);
    setError('');
    setOtp(['', '', '', '']);

    try {
      const result = await sendOTP(formData.phoneNumber, formData.category);

      if (result.success) {
        setResendTimer(60);
      } else {
        setError(result.message || 'Failed to resend OTP. Please try again.');
      }
    } catch (err) {
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="post-service-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>
            {step === 1 && 'Post Your Service'}
            {step === 2 && 'Verify Phone Number'}
            {step === 3 && 'Success!'}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Progress Steps */}
        {step < 3 && (
          <div className="progress-steps">
            <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
              <span className="step-number">1</span>
              <span className="step-label">Details</span>
            </div>
            <div className="progress-line"></div>
            <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
              <span className="step-number">2</span>
              <span className="step-label">Verify</span>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="modal-body">
          {/* Step 1: Form */}
          {step === 1 && (
            <form className="service-form" onSubmit={(e) => { e.preventDefault(); handleSendOTP(); }}>

              {/* Company Details Section */}
              <div className="form-section">
                <strong>Company Details</strong>

                <div className="form-group">
                  <label className="form-label required">Company Name</label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    placeholder="e.g. Royal Travels Pvt Ltd"
                    className="form-input"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required">State</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      placeholder="e.g. Kerala"
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label required">City</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="e.g. Kochi"
                      className="form-input"
                    />
                  </div>
                </div>
              </div>

              {/* Service Information Section */}
              <div className="form-section">
                <strong>Service Information</strong>

                <div className="form-group">
                  <label className="form-label required">Service Headline</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="e.g. Luxury Innova Crysta Fleet for B2B"
                    className="form-input"
                    maxLength={200}
                  />
                  <small className="form-text text-muted">Make it catchy! This is the first thing users see.</small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required">Category</label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="">Select a category</option>
                      {CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Price / Rate</label>
                    <input
                      type="text"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      placeholder="e.g. ₹14/km or ₹2500/night"
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label required">Detailed Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Describe your service, inclusions, exclusions, vehicle condition, hotel amenities, etc."
                    className="form-textarea"
                    rows={4}
                    maxLength={5000}
                  />
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="form-section">
                <strong>Contact Information</strong>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required">Calling Number</label>
                    <div className="phone-input">
                      <span className="phone-prefix">+91</span>
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        placeholder="9876543210"
                        className="form-input"
                        maxLength={10}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label required">WhatsApp Number</label>
                    <div className="phone-input">
                      <span className="phone-prefix">+91</span>
                      <input
                        type="tel"
                        name="whatsappNumber"
                        value={formData.whatsappNumber}
                        onChange={handleInputChange}
                        placeholder="9876543210"
                        className="form-input"
                        maxLength={10}
                        disabled={formData.sameAsPhone}
                      />
                    </div>
                  </div>
                </div>

                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="sameAsPhone"
                    name="sameAsPhone"
                    checked={formData.sameAsPhone}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="sameAsPhone">WhatsApp number is same as calling number</label>
                </div>

                <div className="form-group">
                  <label className="form-label">Email Address (Optional)</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="sales@royaltravels.com"
                    className="form-input"
                  />
                </div>
              </div>

              {error && (
                <div className="error-message">
                  <AlertCircle size={18} />
                  {error}
                </div>
              )}

              <div className="form-actions">
                <button type="submit" className="btn btn-primary btn-primary-full btn-lg" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader className="spinner-icon" size={18} />
                      Sending OTP...
                    </>
                  ) : (
                    'Verify & Publish'
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Step 2: OTP Verification */}
          {step === 2 && (
            <div className="otp-section">
              <div className="otp-info">
                <p>We've sent a 4-digit OTP to</p>
                <p className="otp-phone">+91 {formData.phoneNumber}</p>
              </div>

              <div className="otp-inputs">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    inputMode="numeric"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    className="otp-input"
                    maxLength={1}
                    autoFocus={index === 0}
                  />
                ))}
              </div>

              <div className="otp-resend">
                {resendTimer > 0 ? (
                  <span className="resend-timer">Resend OTP in {resendTimer}s</span>
                ) : (
                  <button
                    className="resend-btn"
                    onClick={handleResendOTP}
                    disabled={loading}
                  >
                    Resend OTP
                  </button>
                )}
              </div>

              {error && (
                <div className="error-message">
                  <AlertCircle size={18} />
                  {error}
                </div>
              )}

              <div className="otp-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setStep(1)}
                  disabled={loading}
                >
                  <ChevronLeft size={18} />
                  Back
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleVerifyOTP}
                  disabled={loading || otp.join('').length !== 4}
                >
                  {loading ? (
                    <>
                      <Loader className="spinner-icon" size={18} />
                      Verifying...
                    </>
                  ) : (
                    'Verify & Submit'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Success */}
          {step === 3 && (
            <div className="success-section">
              <div className="success-icon">
                <CheckCircle size={64} />
              </div>
              <h3>Service Listed Successfully!</h3>
              <p>Your service is now live on TravelB2B Hub. Travel agents can now discover and contact you directly.</p>
              <button className="btn btn-primary btn-lg" onClick={onClose}>
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PostServiceModal;
